/* MSE_TRUST_RATING_V1_20261006 */
(() => {
  'use strict';

  const API = 'https://traceamp.msebeauty.de/api/reviews/product-id/';
  const cache = new Map();

  const fetchSummary = (productId) => {
    if (!cache.has(productId)) {
      cache.set(
        productId,
        fetch(API + encodeURIComponent(productId) + '/summary', {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        }).then((response) => {
          if (!response.ok) throw new Error('MSE Trust HTTP ' + response.status);
          return response.json();
        })
      );
    }
    return cache.get(productId);
  };

  const render = (root, data) => {
    const stars = root.querySelector('[data-mse-trust-rating-stars]');
    const countNode = root.querySelector('[data-mse-trust-rating-count]');
    if (!stars || !countNode) return;

    const count = Number(data?.rating_count) || 0;
    const average = Number(data?.rating_average);
    const rating = Number.isFinite(average) && count > 0
      ? Math.max(0, Math.min(5, average))
      : 0;

    stars.style.setProperty('--rating', String(rating));
    stars.style.setProperty('--rating-max', '5');

    if (count > 0) {
      countNode.textContent = '(' + count + ')';
      countNode.hidden = false;
      root.setAttribute(
        'aria-label',
        rating.toLocaleString(document.documentElement.lang || 'de-DE', {
          minimumFractionDigits: rating % 1 === 0 ? 0 : 1,
          maximumFractionDigits: 1
        }) + ' von 5 Sternen, ' + count + ' Bewertungen'
      );
    } else {
      countNode.textContent = '';
      countNode.hidden = true;
      root.setAttribute('aria-label', 'Noch keine Bewertungen');
    }

    root.classList.remove('is-loading');
  };

  const load = async (root) => {
    if (!root || root.dataset.mseTrustLoaded === '1') return;
    const productId = String(root.dataset.productId || '').trim();
    if (!productId) return;

    root.dataset.mseTrustLoaded = '1';
    root.classList.add('is-loading');

    try {
      render(root, await fetchSummary(productId));
    } catch (error) {
      root.dataset.mseTrustLoaded = '0';
      render(root, null);
    }
  };

  let observer = null;

  const observe = (root) => {
    if (root.dataset.mseTrustObserved === '1') return;
    root.dataset.mseTrustObserved = '1';

    if (!('IntersectionObserver' in window)) {
      load(root);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            load(entry.target);
          });
        },
        { rootMargin: '500px 0px' }
      );
    }

    observer.observe(root);
  };

  const loadCount = async (node) => {
    if (!node || node.dataset.mseTrustCountLoaded === '1') return;
    const productId = String(node.dataset.productId || '').trim();
    if (!productId) return;

    node.dataset.mseTrustCountLoaded = '1';

    try {
      const data = await fetchSummary(productId);
      const count = Number(data?.rating_count) || 0;
      node.textContent = count > 0 ? ' (' + count + ')' : '';
    } catch (error) {
      node.dataset.mseTrustCountLoaded = '0';
      node.textContent = '';
    }
  };

  /* MSE_SPEED_V1_VISIBLE_REVIEW_COUNTS_20261015: defer below-fold review API requests. */
  let countObserver = null;
  const observeCount = (node) => {
    if (!node || node.dataset.mseTrustCountObserved === '1') return;
    node.dataset.mseTrustCountObserved = '1';

    if (!('IntersectionObserver' in window)) {
      loadCount(node);
      return;
    }

    if (!countObserver) {
      countObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            countObserver.unobserve(entry.target);
            loadCount(entry.target);
          });
        },
        { rootMargin: '250px 0px' }
      );
    }
    countObserver.observe(node);
  };

  const scan = (scope = document) => {
    scope.querySelectorAll('[data-mse-trust-rating]').forEach(observe);
    scope.querySelectorAll('[data-mse-trust-review-count]').forEach(observeCount);
  };

  const start = () => {
    scan();

    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.('[data-mse-trust-rating]')) observe(node);
          scan(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();